<template>
    <div v-if="player.team" class="player-page">
        <section class="player-hero" :style="{ '--team-color': player.team.color || '#009688' }">
            <div class="player-hero__inner">
                <!-- the photo on the colour of the team -->
                <div class="player-hero__photo">
                    <img
                        :src="`${assetsUrl}/${player.image}`"
                        :alt="player.name"
                        @error="$event.target.src = defaultPlayerImg"
                    >
                    <span class="player-hero__number">{{ player.alternativeJersey || player.jersey }}</span>
                </div>

                <div class="player-hero__text">
                    <router-link :to="{ name: 'Team', params: { id: player.team.id } }" class="player-hero__team">
                        <span class="cl-logo player-hero__team-logo">
                            <img v-if="player.team.logo" :src="`${assetsUrl}/${player.team.logo}`" :alt="player.team.name">
                            <v-icon v-else small color="teal"> mdi-basketball-hoop </v-icon>
                        </span>
                        {{ player.team.name }}
                    </router-link>
                    <h1 class="player-hero__name">{{ player.name }}<template v-if="player.surname"> {{ player.surname }}</template></h1>
                    <div class="player-hero__chips">
                        <span v-if="player.jersey" class="cl-chip cl-chip--gradient">#{{ player.jersey }}</span>
                        <span v-if="player.position" class="cl-chip">{{ player.position }}</span>
                    </div>
                    <!-- the athlete's health card, filled in, to print for the doctor -->
                    <health-card-button :player="player" />
                    <div class="cl-kpis">
                        <div v-if="player.height" class="cl-kpi">
                            <div class="cl-kpi__value">{{ player.height }}</div>
                            <div class="cl-kpi__label">Ύψος</div>
                        </div>
                        <div v-if="player.birthdayYear || player.birthday" class="cl-kpi">
                            <div class="cl-kpi__value">{{ player.birthdayYear || player.birthday }}</div>
                            <div class="cl-kpi__label">Έτος γέννησης</div>
                        </div>
                        <div v-if="type !== 'cup'" class="cl-kpi" :class="{ 'cl-kpi--accent': playedMatches >= noOfMatches }">
                            <div class="cl-kpi__value" :class="playedMatches < noOfMatches ? 'player-hero__short' : ''">{{ `${playedMatches} / ${noOfMatches}` }}</div>
                            <div class="cl-kpi__label">Αγώνες</div>
                        </div>
                    </div>
                    <div class="mt-5">
                        <match-type-choice @setType="setType" />
                    </div>
                </div>

                <div v-if="topStats" class="player-hero__radar">
                    <radar :player-data="getAverageStats()" :top-stats="topStats" :style="!$vuetify.breakpoint.mobile ? 'height: 220px !important; width: 320px !important;' : 'height: 160px !important; width: auto !important;'" />
                </div>
            </div>
        </section>

        <div class="cl-page">
            <v-tabs v-model="tab" class="cl-tabs mb-6" background-color="transparent" show-arrows>
                <v-tabs-slider class="custom-teal" />
                <v-tab v-for="(item, index) in player.highlights ? items.concat('BIG Moments') : items" :key="index">{{ item }}</v-tab>
            </v-tabs>

            <template v-if="tab === 0">
                <player-stats :total-stats="getCummulativeStats()" :avg-stats="getAverageStats()" />
                <section v-if="player.stats.length > 0 && player.stats.some(stat => stat.throwPositions)" class="cl-section mt-8">
                    <scroll-strip class="player-page__charts">
                        <template #title>
                            <section-heading title="Shooting Charts" icon="mdi-target" />
                        </template>
                        <template v-for="(stat, index) in player.stats">
                            <div v-if="stat.throwPositions" :key="`stat-${index}`" class="cl-card player-chart" @click="$router.push({ name: 'Match', params: { id: stat.matchId } })">
                                <div class="player-chart__head">
                                    <span class="cl-logo player-chart__logo">
                                        <img :src="`${assetsUrl}/${stat.opponentTeamLogo}`" alt="">
                                    </span>
                                    <div>
                                        <div class="player-chart__opponent">vs {{ stat.opponentTeamName }}</div>
                                        <div class="player-chart__date">{{ new Date(stat.matchDatetime).toLocaleDateString('el-GR') }}</div>
                                    </div>
                                </div>
                                <shot-summary :stat="stat" class="player-chart__summary" />
                                <shooting-chart :shots="stat.throwPositions" />
                                <div class="player-chart__legend">
                                    <span><v-icon x-small color="#03e5b7">mdi-circle</v-icon>{{ courtCount(stat, true) }} εύστοχα στο γήπεδο</span>
                                    <span><v-icon x-small color="#ff5a5a">mdi-close-thick</v-icon>{{ courtCount(stat, false) }} άστοχα</span>
                                </div>
                            </div>
                        </template>
                    </scroll-strip>
                </section>
                <game-stats :stats="player.stats.filter(element => (type === 'cup' ? (type === 'cup' && element.cup) : (element.phase === type && !element.cup)))" />
            </template>
            <player-records v-else-if="tab === 1" :stats="getMaxStats()" :team="player.team.name" />
            <player-moments v-else :moments="player.highlights" />
        </div>
    </div>
</template>

<script>
    import routeTab from '@/mixins/routeTab'
    import PlayerStats from '@/components/PlayerStats'
    import ShootingChart from '@/components/ShootingChart'
    import ShotSummary from '@/components/ShotSummary'
    import ScrollStrip from '@/components/ScrollStrip'
    import PlayerMoments from '@/components/PlayerMoments'
    import GameStats from '@/components/GameStats'
    import PlayerRecords from '@/components/PlayerRecords'
    import playerService from '@/services/player'
    import configService from '@/services/config'
    import MatchTypeChoice from '@/components/MatchTypeChoice'
    import Radar from '@/components/Radar.vue'
    import SectionHeading from '@/components/SectionHeading'
    import { foulsOf } from '@/utils/teamStats'
    import HealthCardButton from '@/components/HealthCardButton'

    const gradients = [
        ['#ffd200'],
        ['#ffd200'],
        ['red', 'teal', 'yellow'],
        ['purple', 'violet'],
        ['#ffd200', '#F0F', '#FF0'],
        ['#f72047', '#ffd200', '#ffd200']
    ]

    export default {
        components: { PlayerStats, GameStats, PlayerRecords, PlayerMoments, MatchTypeChoice, Radar, ShootingChart, ShotSummary, ScrollStrip, SectionHeading, HealthCardButton },
        mixins: [routeTab],
        data() {
            return {
                player: {},
                items: ['Stats', 'Records'],
                tab: null,
                assetsUrl: 'https://storage.googleapis.com',
                showIcon: false,
                noOfMatches: 0,
                type: 'regular_season',
                matchTypes: ['regular_season', 'playoffs', 'cup'],
                sparkline: {
                    width: 2,
                    radius: 10,
                    padding: 8,
                    lineCap: 'round',
                    gradient: gradients[5],
                    gradientDirection: 'top',
                    gradients,
                    fill: false,
                    type: 'trend',
                    autoLineWidth: false
                },
                topStats: null,
                defaultPlayerImg: require('@/assets/default_player.png')
            }
        },
        computed: {
            // the regular season (or cup) matches the player has played
            playedMatches() {
                return (this.player.stats || []).filter(element => ((element.phase === 'regular_season') || (this.type === 'cup' && element.cup))).length
            }
        },
        watch: {
            '$route.params.id': {
                handler() {
                    this.getPlayer(this.$route.params.id)
                }
            },
            'player.noOfMatches': {
                handler() {
                    this.getNoOfMatches()
                },
                deep: true
            }
        },
        created() {
            this.getPlayer(this.$route.params.id)
            this.getTopStats()
        },
        methods: {
            // the shots of a match on the court, made or missed
            courtCount(stat, made) {
                return (stat.throwPositions || []).filter(shot => (shot.throwStatus === 'made') === made).length
            },
            getShootingChartData() {
                this.player.stats.filter(element => (element.phase === this.type || (this.type === 'cup' && element.cup))).forEach(element => {
                    if (element.throwPositions) {
                        element.throwPositions.forEach(shot => {
                            this.shots.push(shot)
                        })
                    }
                })
                return this.shots
            },
            setType(option) {
                this.type = this.matchTypes[option]
            },
            getPlayer(playerId) {
                playerService.getPlayer(playerId).then(resp => {
                    this.player = resp.data
                })
            },
            getTopStats() {
                playerService.getTopStats().then(resp => {
                    this.topStats = resp.data
                })
            },
            getCummulativeStats() {
                const stats = {
                    points: 0,
                    rebounds: 0,
                    assists: 0,
                    steals: 0,
                    turnovers: 0,
                    blockedShots: 0,
                    twoPointsMade: 0,
                    twoPointAttempts: 0,
                    threePointsMade: 0,
                    threePointAttempts: 0,
                    freeThrowsMade: 0,
                    freeThrowAttempts: 0,
                    offensiveRebounds: 0,
                    defensiveRebounds: 0,
                    personalFouls: 0,
                    fouls: 0,
                    pir: 0,
                    playTimeInSecs: 0
                }
                this.player.stats.filter(element => (element.phase === this.type || (this.type === 'cup' && element.cup))).forEach(element => {
                    for (const key of Object.keys(stats)) {
                        stats[key] += key === 'fouls' ? foulsOf(element) : element[key]
                    }
                })
                return stats
            },
            getAverageStats() {
                const stats = {
                    points: 0,
                    rebounds: 0,
                    assists: 0,
                    steals: 0,
                    turnovers: 0,
                    blockedShots: 0,
                    twoPointsMade: 0,
                    twoPointAttempts: 0,
                    threePointsMade: 0,
                    threePointAttempts: 0,
                    freeThrowsMade: 0,
                    freeThrowAttempts: 0,
                    offensiveRebounds: 0,
                    defensiveRebounds: 0,
                    personalFouls: 0,
                    fouls: 0,
                    pir: 0,
                    playTimeInSecs: 0
                }
                this.player.stats.filter(element => (element.phase === this.type || (this.type === 'cup' && element.cup))).forEach(element => {
                    for (const key of Object.keys(stats)) {
                        stats[key] += key === 'fouls' ? foulsOf(element) : element[key]
                    }
                })

                Object.keys(stats).forEach(element => {
                    if (this.player.stats.filter(element => (element.phase === this.type || (this.type === 'cup' && element.cup))).length) {
                        stats[element] = Math.round(stats[element] / this.player.stats.filter(element => (element.phase === this.type || (this.type === 'cup' && element.cup))).length * 10) / 10
                    } else {
                        stats[element] = 0
                    }
                })

                return stats
            },
            getMaxStats() {
                const stats = {
                    points: {
                        value: 0,
                        oponnent: null,
                        date: null,
                        text: 'points'
                    },
                    rebounds: {
                        value: 0,
                        oponnent: null,
                        date: null,
                        text: 'reb.'
                    },
                    assists: {
                        value: 0,
                        oponnent: null,
                        date: null,
                        text: 'assists'
                    },
                    steals: {
                        value: 0,
                        oponnent: null,
                        date: null,
                        text: 'steals'
                    },
                    turnovers: {
                        value: 0,
                        oponnent: null,
                        date: null,
                        text: 'turnov.'
                    },
                    blockedShots: {
                        value: 0,
                        oponnent: null,
                        date: null,
                        text: 'blocks'
                    },
                    twoPointsMade: {
                        value: 0,
                        oponnent: null,
                        date: null
                    },
                    threePointsMade: {
                        value: 0,
                        oponnent: null,
                        date: null
                    },
                    freeThrowsMade: {
                        value: 0,
                        oponnent: null,
                        date: null
                    }
                }
                this.player.stats.filter(element => (element.phase === this.type || (this.type === 'cup' && element.cup))).forEach(element => {
                    for (const key of Object.keys(stats)) {
                        if (element[key] >= stats[key].value) {
                            stats[key] = {
                                matchId: element.matchId,
                                value: element[key],
                                oponnent: element.opponentTeamName,
                                oponnentLogo: element.opponentTeamLogo,
                                date: element.matchDatetime
                            }
                        }
                    }
                })

                return stats
            },
            getNoOfMatches() {
                configService.getConfigField(this.player.noOfMatches).then(resp => {
                    this.noOfMatches = resp.data[this.player.noOfMatches]
                })
            }
        }
    }
</script>

<style scoped>
.player-hero {
    position: relative;
    overflow: hidden;
    color: #fff;
    text-align: left;
    background:
        radial-gradient(circle at 92% 10%, rgba(3, 229, 183, 0.25) 0%, rgba(3, 229, 183, 0) 45%),
        #232323;
}

.player-hero::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 4px;
    background: linear-gradient(90deg, #03e5b7 0%, #037ade 74%);
}

.player-hero__inner {
    position: relative;
    display: flex;
    align-items: flex-end;
    gap: 32px;
    max-width: 1240px;
    margin: 0 auto;
    padding: 32px 24px 0;
}

.player-hero__photo {
    position: relative;
    flex: 0 0 auto;
    width: 240px;
    height: 280px;
    display: flex;
    align-items: flex-end;
    justify-content: center;
}

/* the slanted panel in the colour of the team */
.player-hero__photo::before {
    content: '';
    position: absolute;
    left: 10px;
    right: 10px;
    top: 30px;
    bottom: 0;
    border-radius: 12px 12px 0 0;
    background: var(--team-color);
    transform: skewX(-6deg);
}

.player-hero__photo img {
    position: relative;
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    object-position: bottom;
}

.player-hero__number {
    position: absolute;
    top: 36px;
    left: 22px;
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 3.2rem;
    line-height: 1;
    text-shadow: 0 2px 10px rgba(0, 0, 0, 0.4);
}

.player-hero__text {
    flex: 1 1 auto;
    min-width: 0;
    padding-bottom: 28px;
}

.player-hero__team {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: rgba(255, 255, 255, 0.75) !important;
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.85rem;
    font-weight: 700;
    text-decoration: none;
}

.player-hero__team:hover {
    color: #03e5b7 !important;
}

.player-hero__team-logo {
    width: 28px;
    height: 28px;
    border-radius: 6px;
}

.player-hero__name {
    margin: 8px 0 10px;
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 3rem;
    font-weight: 400;
    line-height: 1;
    text-transform: uppercase;
    overflow-wrap: anywhere;
}

.player-hero__chips {
    display: flex;
    gap: 6px;
    margin-bottom: 14px;
    text-transform: capitalize;
}

.player-hero__short {
    color: #ff6b6b;
}

.player-hero__radar {
    flex: 0 0 auto;
    align-self: center;
    margin-bottom: 28px;
    padding: 10px;
    border-radius: 12px;
    background: #fff;
}

.player-chart {
    flex: 0 0 auto;
    width: 378px;
    max-width: calc(100vw - 48px);
    padding: 14px;
    cursor: pointer;
}

.player-chart__summary {
    margin-bottom: 12px;
}

.player-chart__legend {
    display: flex;
    gap: 14px;
    margin-top: 8px;
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.74rem;
    color: rgba(255, 255, 255, 0.65);
}

.player-chart__legend span {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.player-chart__head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;
    font-family: Roboto, Arial, sans-serif;
}

.player-chart__logo {
    width: 40px;
    height: 40px;
}

.player-chart__opponent {
    font-weight: 700;
}

.player-chart__date {
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.6);
}

@media (max-width: 900px) {
    .player-hero__inner {
        flex-wrap: wrap;
        gap: 16px;
        padding: 20px 16px 0;
    }

    .player-hero__photo {
        width: 160px;
        height: 200px;
    }

    .player-hero__name {
        font-size: 2.2rem;
    }

    .player-hero__radar {
        width: 100%;
    }
}
</style>

<style scoped>
.overlay {
    opacity: 0.3;
    filter: grayscale(1);
}

.text-subtitle-1 {
    font-size: 1.1rem !important;
    font-weight: 400;
    line-height: 1.75rem;
    letter-spacing: 0.009375em !important;
}

.text-h6 {
    font-size: 1.25rem !important;
    letter-spacing: 0.0125em !important;
    line-height: 2rem;
}

.bottom-border {
    border-bottom: 0.01px solid rgb(231, 225, 225);
}
</style>
